import React, { useEffect, useState } from 'react';

import {
  Dialog,
  DialogActions,
  DialogContent,
  Skeleton, Stack,
} from '@mui/material';

import Button from '../../../components/Button';
import LessonDetails from '../../../components/LessonDetails';
import Navigator from '../../../components/Navigator';
import SkeletonStack from '../../../components/SkeletonStack';
import StudentHomeworkRow from '../../../components/StudentHomeworkRow';
import StudentRow from '../../../components/StudentRow';
import {
  useGetLessonEnrolmentQuery,
  useGetLessonQuery,
  useGetStudentLessonEnrolmentsQuery,
  useMarkRollMutation,
  useUnenrolFromLessonMutation,
  useUnmarkRollMutation,
  useUpdateLessonEnrolmentMutation,
  useUpdateLessonMutation,
} from '../../../services/classes';
import { Lesson, LessonEnrolment } from '../../../services/classes/types';
import { useGetCoursesQuery } from '../../../services/courses';
import { useGetStaffQuery } from '../../../services/staff';
import { useGetStudentQuery } from '../../../services/students';
import { Student } from '../../../services/students/types';
import { AttendanceType, EnrolmentType } from '../../../types/common';
import {
  getClassLessonIdentifier,
  getLessonClass,
  getLessonCourse,
  getLessonEnrolmentClass,
  getLessonEnrolmentCourse,
  getLessonEnrolmentLessonId,
  getLessonEnrolmentSubject,
  getLessonSubject,
} from '../../../utils/common/classes';
import useError from '../../../utils/react/hooks/useError';
import useSuccess from '../../../utils/react/hooks/useSuccess';

interface LessonStudentRowProps {
  studentId: string,
  lesson: Lesson,
  addStudentDetails?: (student: Student) => void,
  isAdmin: boolean,
}

const LessonStudentRow = ({ studentId,
  addStudentDetails,
  lesson,
  isAdmin }: LessonStudentRowProps) => {
  const { data: student, isLoading } = useGetStudentQuery({ studentId });
  const [updateLessonEnrolment] = useUpdateLessonEnrolmentMutation();

  const subject = getLessonSubject(lesson);
  const classId = getLessonClass(lesson);

  const { data: lessonEnrolment,
    isLoading: isLessonEnrolmentLoading } = useGetLessonEnrolmentQuery({
    subject,
    classId,
    lesson: lesson.lesson,
    studentId,
  });

  const [unenrolStudent, {
    isError: isUnenrolStudentError,
    error: unenrolStudentError,
    isSuccess: isUnenrolStudentSuccess,
  }] = useUnenrolFromLessonMutation();

  const handleMarkStudent = (id: string, present: boolean) => {
    updateLessonEnrolment({
      subject,
      classId,
      lesson: lesson.lesson,
      studentId: id,
      newAttributes: {
        present,
      },
    });
  };

  const handleToggleStudentOnline = (id: string) => {
    updateLessonEnrolment({
      subject,
      classId,
      lesson: lesson.lesson,
      studentId: id,
      newAttributes: {
        attendanceType: lessonEnrolment?.data.attendanceType === AttendanceType.Online ?
          AttendanceType.InPerson : AttendanceType.Online,
      },
    });
  };

  const handleLessonUnenrol = (id: string) => {
    unenrolStudent({
      subject,
      classId,
      lesson: lesson.lesson,
      studentId: id,
    });
  };

  useError(isUnenrolStudentError, unenrolStudentError);
  useSuccess(isUnenrolStudentSuccess, 'Student unenrolled');

  useEffect(() => {
    if (student?.data && addStudentDetails) {
      addStudentDetails(student.data);
    }
  }, [student, lesson]);

  if (isLoading || isLessonEnrolmentLoading) {
    return (
      <Skeleton />
    );
  }

  if (student?.data && lessonEnrolment?.data) {
    return (
      <StudentRow
        student={student.data}
        studentDetailsHref={isAdmin ? `/admin/student/${student.data.studentId}` : undefined}
        editable={!lesson.rollMarked}
        captionAttributes={isAdmin ? ['phoneNumber', 'school'] : ['school']}
        isPresent={lessonEnrolment.data.present}
        isMakeup={lessonEnrolment.data.enrolmentMethod === EnrolmentType.Makeup}
        isOnline={lessonEnrolment.data.attendanceType === AttendanceType.Online}
        onOnlineToggle={isAdmin ? handleToggleStudentOnline : undefined}
        onMark={handleMarkStudent}
        makeupHref={isAdmin ? `/roll/${subject}/${classId}/${encodeURIComponent(lesson.lesson)}/reschedule/${studentId}` : undefined}
        handleLessonUnenrol={isAdmin ? handleLessonUnenrol : undefined}
      />
    );
  }
  return (
    <div />
  );
};

interface HomeworkRowProps {
  studentId: string,
  lesson: Lesson,
  editable?: boolean,
  offset: number,
  updateHomework: (lessonEnrolment: LessonEnrolment, complete?: boolean) => void,
  isError: boolean,
}

const HomeworkRow = ({ studentId,
  lesson,
  editable = false,
  offset,
  updateHomework,
  isError }: HomeworkRowProps) => {
  const { data: student, isLoading } = useGetStudentQuery({ studentId });
  const { data: lessonEnrolments } = useGetStudentLessonEnrolmentsQuery({
    studentId: student?.data.id ?? '',
  }, { skip: !student?.data });

  const [updatedLessonEnrolments, setUpdatedLessonEnrolments] = useState(lessonEnrolments?.data);

  useEffect(() => {
    if (lessonEnrolments?.data) {
      setUpdatedLessonEnrolments(lessonEnrolments?.data?.filter((lessonEnrolment) =>
        getLessonEnrolmentCourse(lessonEnrolment) === getLessonCourse(lesson)));
    }
  }, [lessonEnrolments]);

  useEffect(() => {
    if (isError) {
      setUpdatedLessonEnrolments(lessonEnrolments?.data);
    }
  }, [isError]);

  const handleMarkHomework = (lessonEnrolmentId: string, complete?: boolean) => {
    setUpdatedLessonEnrolments((prev) => prev?.map((lessonEnrolment) => {
      if (lessonEnrolment.id === lessonEnrolmentId) {
        updateHomework(lessonEnrolment, complete);

        return {
          ...lessonEnrolment,
          homework: {
            complete,
          },
        };
      }
      return lessonEnrolment;
    }));
  };

  if (isLoading) {
    return (
      <Skeleton />
    );
  }

  if (student?.data && updatedLessonEnrolments) {
    return (
      <StudentHomeworkRow
        student={student.data}
        lessonEnrolments={updatedLessonEnrolments}
        selectedClassLessonIdentifier={getClassLessonIdentifier(lesson)}
        markHomework={handleMarkHomework}
        editable={editable}
        offset={offset}
      />
    );
  }
  return (
    <div />
  );
};

type HomeworkToUpdate = {
  [lessonEnrolmentId: string]: {
    subject: string,
    classId: string,
    lessonId: string,
    studentId: string,
    homework: {
      complete?: boolean,
    },
  }
};

interface RollLessonDetailsProps {
  selectedLesson: Lesson;
  onClose: () => void;
  isAdmin: boolean;
  isTeacher: boolean;
}

const RollLessonDetails = ({
  selectedLesson,
  onClose = () => {},
  isAdmin,
  isTeacher,
}: RollLessonDetailsProps) => {
  const { data: courses, isLoading: isCoursesLoading } = useGetCoursesQuery({});

  const { data: teacher, isLoading: isTeacherLoading } = useGetStaffQuery({
    staffId: selectedLesson.teacher,
  }, { skip: !selectedLesson.teacher });

  const subject = getLessonSubject(selectedLesson);
  const classId = getLessonClass(selectedLesson);

  const { data: fetchedLesson,
    isLoading: isLessonLoading } = useGetLessonQuery({
    subject, classId, lesson: selectedLesson.lesson,
  });

  const [lesson, setLesson] = useState<Lesson>(selectedLesson);

  const [students, setStudents] = useState<{
    [lessonId: string]: Student[],
  }>({});

  const [isHomeworkOpen, setIsHomeworkOpen] = useState(false);
  const [isHomeworkEditable, setIsHomeworkEditable] = useState(false);
  const [selectedOffset, setSelectedOffset] = useState(0);

  const handleAddStudentDetails = (student: Student) => {
    setStudents((prev) => {
      const newStudents = prev;
      if (!newStudents[lesson.id]) {
        newStudents[lesson.id] = [];
      }

      if (!newStudents[lesson.id].find((s) => s.id === student.id)) {
        newStudents[lesson.id].push(student);
      }

      return newStudents;
    });
  };

  const handleCopyStudentPhoneNumbers = () => {
    const phoneNumbers = students[lesson.id].map((s) => s.phoneNumber).join(', ');
    navigator.clipboard.writeText(phoneNumbers);
  };

  useEffect(() => {
    if (fetchedLesson) {
      setLesson(fetchedLesson.data);
    }
  }, [fetchedLesson]);

  const [updateLesson] = useUpdateLessonMutation();
  const [markRoll, { isLoading: isMarkRollLoading,
    isError: isMarkRollError,
    error: markRollError }] = useMarkRollMutation();
  const [unmarkRoll, { isLoading: isUnmarkRollLoading,
    isError: isUnmarkRollError,
    error: unmarkRollError }] = useUnmarkRollMutation();

  const handleSubmitRoll = () => {
    if (!lesson.rollMarked) {
      markRoll({
        subject,
        classId,
        lesson: lesson.lesson,
      });
    } else {
      unmarkRoll({
        subject,
        classId,
        lesson: lesson.lesson,
      });
    }
  };

  useError(isMarkRollError, markRollError);
  useError(isUnmarkRollError, unmarkRollError);

  const [homeworkToUpdate, setHomeworkToUpdate] = useState<HomeworkToUpdate>({});

  const [updateLessonEnrolment, {
    isLoading: isHomeworkUpdateLoading,
    isError: isHomeworkUpdateError,
    error: homeworkUpdateError,
  }] =
  useUpdateLessonEnrolmentMutation();

  useError(isHomeworkUpdateError, homeworkUpdateError);

  const handleSaveHomework = () => {
    if (isHomeworkEditable) {
      Object.values(homeworkToUpdate).forEach((value) => {
        updateLessonEnrolment({
          subject: value.subject,
          classId: value.classId,
          lesson: value.lessonId,
          studentId: value.studentId,
          newAttributes: {
            homework: value.homework,
          },
        });
      });

      setHomeworkToUpdate({});

      updateLesson({
        subject,
        classId,
        lesson: lesson.lesson,
        newAttributes: {
          homeworkMarked: true,
        },
      });

      setIsHomeworkEditable(false);
    } else {
      setIsHomeworkEditable(true);
    }
  };

  const handleHomeworkClose = () => {
    setHomeworkToUpdate({});
    setIsHomeworkOpen(false);
    setIsHomeworkEditable(false);
  };

  const handleHomeworkChange = (
    lessonEnrolment: LessonEnrolment, complete?: boolean) => {
    setHomeworkToUpdate((prev) => {
      const newHomework = prev;
      newHomework[lessonEnrolment.id] = {
        subject: getLessonEnrolmentSubject(lessonEnrolment),
        classId: getLessonEnrolmentClass(lessonEnrolment),
        lessonId: getLessonEnrolmentLessonId(lessonEnrolment),
        studentId: lessonEnrolment.student,
        homework: {
          complete,
        },
      };

      return newHomework;
    });
  };

  if (isLessonLoading || isCoursesLoading || isTeacherLoading) {
    return (
      <SkeletonStack />
    );
  }

  return (
    <>
      <LessonDetails
        lesson={lesson}
        classDetailsHref={`/${isAdmin ? 'admin' : 'teacher'}/class/${subject}/${classId}`}
        courses={courses?.data ?? []}
        teacher={selectedLesson.teacher ? teacher?.data : undefined}
        onClose={onClose}
        toggleRoll={handleSubmitRoll}
        updateLesson={(newAttributes) => updateLesson(
          {
            subject, classId, lesson: selectedLesson.lesson, newAttributes,
          },
        )}
        disableTimeEdit={!isAdmin}
        isRollLoading={isMarkRollLoading || isUnmarkRollLoading}
        onSMS={isAdmin ? handleCopyStudentPhoneNumbers : undefined}
        toggleHomework={isTeacher ? () => setIsHomeworkOpen((prev) => !prev) : undefined}
        renderStudentRow={(studentId) => (
          <LessonStudentRow
            key={studentId}
            studentId={studentId}
            lesson={lesson}
            addStudentDetails={isAdmin ? handleAddStudentDetails : undefined}
            isAdmin={isAdmin}
          />
        )}
      />
      {isHomeworkOpen && (
        <Dialog
          title="Homework"
          onClose={handleHomeworkClose}
          open={isHomeworkOpen}
          fullWidth
          maxWidth="lg"
        >
          <DialogContent>
            <Stack
              direction="column"
            >
              <Stack
                justifyContent="center"
                alignItems="flex-end"
              >
                <Navigator
                  onNavigateForward={() => setSelectedOffset((prev) => prev + 1)}
                  onNavigateBackward={() => setSelectedOffset((prev) => prev - 1)}
                  onNavigateDefault={() => setSelectedOffset(0)}
                  defaultText="Today"
                  forwardDisabled={selectedOffset === 0}
                  defaultDisabled={selectedOffset === 0}
                />
              </Stack>
              {lesson.enrolledStudents.map((student) => (
                <HomeworkRow
                  key={student.studentId}
                  studentId={student.studentId}
                  lesson={lesson}
                  editable={isHomeworkEditable}
                  offset={selectedOffset}
                  updateHomework={handleHomeworkChange}
                  isError={isHomeworkUpdateError}
                />
              ))}
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={handleSaveHomework}
              loading={isHomeworkUpdateLoading}
              variant={isHomeworkEditable ? 'contained' : 'outlined'}
              color={isHomeworkEditable ? 'darkPrimary' : 'gray'}
            >
              {isHomeworkEditable ? 'Save' : 'Edit'}
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </>
  );
};

export default RollLessonDetails;
